import React, {useEffect, useMemo, useState} from 'react';
import CustomModal from "../../../components/UI/CustomModal";
import useScreenSize from "../../../helpers/hooks/useScreenSize";
import {useDispatch, useSelector} from "react-redux";
import Logo from "../../../components/Logo";
import {Btn, FlexColumnSection, FlexRowSection, Header, Wrapper} from "../../../styles/globalStyles";
import {t} from "i18next";
import {isILPhone, isLocationIL, maskEmail, maskPhone} from "../../../helpers/functions";
import {setKioskError} from "@arboxappv4/shared/src/redux/kioskStandAlone/kioskSlice";
import styled from "styled-components";
import {Colors} from "@arboxappv4/shared/src/styles/Colors";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import iconGenerator from "../../../helpers/IconGenerator";
import {iconPrefix} from "@arboxappv4/shared/src/helpers/constants";
import {
    hideModal,
    KIOSK_TOKEN_VERIFY_MODAL, ONE_TIME_PASSWORD_MODAL,
    openModal, updateModalRes,
} from "@arboxappv4/shared/src/redux/modalManagerSlice";
import FormInput from "../../../components/UI/FormInput";
import {apiAction} from "@arboxappv4/shared/src/helpers/HTTP";
import {isRTL} from "../../../helpers/i18n";

const KioskTokenValidateModal = (props) => {
    const { isOpen, onClose } = props;
    const usersBoxes = useSelector(state => state.kiosk.usersBoxes)
    const selectedLocation = useSelector(state => state.kiosk.selectedLocation)
    const oneTimePasswordModalRes = useSelector(state => state.modalManager[ONE_TIME_PASSWORD_MODAL].processRes)
    const [selectedMessageType, setSelectedMessageType] = useState(null);

    const {isMobile} = useScreenSize();
    const dispatch = useDispatch()

    const sendVia = useMemo(() => {
        let options = null
        if(usersBoxes && selectedLocation) {
            options = []
            if(usersBoxes.phone && (isILPhone(usersBoxes.phone) || (usersBoxes.phone.startsWith('05') && isLocationIL()))) {
                options.push({id: 'phone', icon: 'phone', label: t('phone')})
            }
            if(usersBoxes.user.email) {
                options.push({id: 'email', icon: 'envelope', label: t('email')})
            }
        }
        return options
    }, [usersBoxes, selectedLocation]);

    const maskedEmail = useMemo(() => {
        if(sendVia.some(option => option.id === 'email')) {
            return maskEmail(usersBoxes.user.email)
        }
        return ''
    }, [sendVia]);

    const maskedPhone = useMemo(() => {
        if(sendVia.some(option => option.id === 'phone')) {
            return maskPhone(usersBoxes.phone)
        }
        return ''
    }, [sendVia]);

    const maskedInput = useMemo(() => {
        if(selectedMessageType === 'phone') {
            return maskedPhone
        } else if(selectedMessageType === 'email') {
            return maskedEmail
        }
        return ''
    }, [selectedMessageType]);

    useEffect(() => {
        if(sendVia && sendVia.length === 0) {
            dispatch(setKioskError('no-token-verification-option-error'))
            closeModal()
        } else if (sendVia.length >= 1) {
            setSelectedMessageType(sendVia[0].id)
        }
    }, [sendVia]);

    useEffect(() => {
        if(oneTimePasswordModalRes) {
            dispatch(updateModalRes({modalName: ONE_TIME_PASSWORD_MODAL, res: null}))
            dispatch(updateModalRes({modalName: KIOSK_TOKEN_VERIFY_MODAL, res: {valid: true}}))
            onClose()
        }
    }, [oneTimePasswordModalRes]);

    const sendOneTimeCode = async () => {
        const type = selectedMessageType === 'phone' ? 'sms' : 'email'
        const otpId = await apiAction(`mfa`, 'post', {user_id: usersBoxes.user_fk ,type: type, value: selectedMessageType === 'phone' ? usersBoxes.phone : usersBoxes.user.email});
        let params = {type, otpId, userId: usersBoxes.user_fk, standAlone: true, isTokenVerify: true}
        if(type === 'sms') params.phone = usersBoxes.phone
        else params.email = usersBoxes.user.email
        dispatch(hideModal({modalName: KIOSK_TOKEN_VERIFY_MODAL, hide: true}))
        dispatch(openModal({modalName: ONE_TIME_PASSWORD_MODAL, props: params}))
    }

    const closeModal = () => {
        onClose({fromClose: true})
    }

    return (
        <CustomModal open={isOpen} onCancel={closeModal} width={isMobile ? '100%' : '450px'}>
            <Wrapper>
                <FlexColumnSection gap={'20px'}>
                    {isMobile && <Logo onClick={onClose}/>}
                    <FlexColumnSection gap={'5px'} flex={'none'}>
                        <Header>{t('token-auth-header')}</Header>
                        <div>{t(`token-auth-subheader${sendVia.some(option => option.id === 'phone') ? '-phone' : ''}${sendVia.some(option => option.id === 'email') ? '-email' : ''}`)}</div>
                    </FlexColumnSection>
                    {sendVia?.length > 1 &&
                        <FlexRowSection gap={'20px'}>
                            {sendVia.map(option =>
                                <SendViaOption active={selectedMessageType === option.id}
                                               onClick={() => setSelectedMessageType(option.id)}>
                                    <FontAwesomeIcon
                                        icon={iconGenerator({name: option.icon, type: iconPrefix.REGULAR})}
                                        color={Colors.siteBlack}/>
                                    <span>{option.label}</span>
                                </SendViaOption>
                            )}
                        </FlexRowSection>}
                    <FlexColumnSection>
                        {selectedMessageType && <FormInput mask={true} label={t(`send-via-${selectedMessageType}-input-label`)} disabled={true} initValue={maskedInput} value={maskedInput} flex={1} style={{direction: 'ltr'}}/>}
                    </FlexColumnSection>
                    <Btn type={'primary'} onClick={sendOneTimeCode} width={'fit-content'}>{t('send-sms-opt')}</Btn>
                    <div>
                        <Conditions>{t('agree-to')} </Conditions>
                        <ConditionsLink onClick={() => window.open('https://www.arboxapp.com/terms-and-conditions', '_blank')}>{t('terms-of-Use')}</ConditionsLink>
                        <Conditions> {t('acknowledge-and-read')} </Conditions>
                        <ConditionsLink onClick={() => window.open('https://www.arboxapp.com/privacy-policy', '_blank')}>{t('privacy-policy')}</ConditionsLink>
                    </div>
                </FlexColumnSection>
            </Wrapper>
        </CustomModal>
    );
};

export default KioskTokenValidateModal;

const SendViaOption = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    align-items: center;
    justify-content: center;
    padding: 15px;
    flex: 1;
    border: ${({active}) => `1px solid ${active ? Colors.siteBlack : Colors.siteScheduleBorder}`};
    font-weight: 500;
    border-radius: 5px;
    cursor: pointer;

    :hover {
        border-color: ${Colors.greyText};
    }
`;

const Conditions = styled.span`
    font-size: 12px;
`;

const ConditionsLink = styled(Conditions)`
    text-decoration: underline;
    cursor: pointer;
`;